import type { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appUserClient, commonClient } from 'api';
import type { CappedTableResultOfEventLogEntry, ListEventLogsParams } from 'api/models/api';
import type { ColorPalette, LoggedUserInfo, PublicCountry } from 'api/models/mainservice';
import { defaultEventLogQuery } from 'helpers/auditHelper';
import { handleApiErrorWithNotification } from 'helpers/errorHandling';
import { getInfiniteNextPageParam, getInfiniteNextPageParamV2 } from 'helpers/queriesHelper';
import { getTableQueryKey } from 'helpers/tableHelpers';

import type {
  Announcement,
  ApiError,
  ConfigurationItem,
  GetFeatureFlagsQueryResult,
  PowerBiEmbeddedTokenResult,
  PublicCompanySettings,
  SignalRNotification,
  SignalRNotificationList,
  UserConfigurationItemToEdit
} from './models';

export const GLOBAL_QUERY_STALE_TIME = 3600000; // 1 hour in ms

// 1 minut, stale time for queries which are called with same params in short time and expecting same result
// TODO: better name?
export const MULTICALL_QUERY_STALE_TIME = 60000;

// 3s, global stale time
export const DEFAULT_QUERY_STALE_TIME = 3000;

// 15min
export const DASHBOARD_STALE_TIME = 900000;

// 1min
export const LOGIN_STALE_TIME = 60000;

const companyUserInfoKey = 'companyUserInfo';
const publicCompanySettingsKey = 'publicCompanySettings';
const configurationKey = 'configurationKey';
const emailConfirmKey = 'emailConfirm';
const countriesKey = 'countries';
const notificationsKey = 'notifications';
const powerBIConfig = 'powerBIConfig';
const featureFlagsKey = 'featureFlags';
const currentAnnouncementsKey = 'currentAnnouncementsKey';
const colorPalettesKey = 'colorPalettes';
const eventLogsKey = 'eventLogs';

// Startup must eventually offer retry even when the global network retry policy is unlimited.
const retryStartupQuery = (failureCount: number, error: ApiError) => {
  const status = error.response?.status;
  return failureCount < 2 && (!status || status === 429 || (status >= 500 && status !== 503));
};

export const getCompanyAndUserInfoKeyBuilder = () => [companyUserInfoKey];
export const getPublicCompanySettingsKeyBuilder = (tenantCode: string) => [
  publicCompanySettingsKey,
  tenantCode
];
export const getConfigurationKeyBuilder = () => [configurationKey];
export const getConfigurationKeyBuilderV2 = () => [configurationKey, 'v2'];
export const getCompanyConfigurationKeyBuilderV2 = () => [configurationKey, 'company', 'v2'];
export const getEmailConfirmKeyBuilder = () => [emailConfirmKey];
export const getCountriesKeyBuilder = () => [countriesKey];
export const getNotificationsKeyBuilder = () => [notificationsKey];
export const getPowerBIConfigKeyBuilder = () => [powerBIConfig];
export const getFeatureFlagsKeyBuilder = () => [featureFlagsKey];
export const getCurrentAnnouncementsKeyBuilder = () => [currentAnnouncementsKey];
export const getColorPalettesKeyBuilder = () => [colorPalettesKey];

export const useGetCompanyAndUserInfoQuery = (enabled?: boolean) => {
  return useQuery<LoggedUserInfo, ApiError>(
    getCompanyAndUserInfoKeyBuilder(),
    commonClient.getCompanyAndUserInfo,
    { staleTime: GLOBAL_QUERY_STALE_TIME, enabled: enabled, retry: retryStartupQuery }
  );
};

export const useGetPublicCompanySettingsQuery = (tenantCode?: string) => {
  return useQuery<PublicCompanySettings, ApiError>(
    getPublicCompanySettingsKeyBuilder(tenantCode || ''),
    () => commonClient.getPublicCompanySettings(tenantCode || ''),
    { enabled: !!tenantCode, staleTime: LOGIN_STALE_TIME }
  );
};

export const useConfirmEmailQuery = (userId: string, token: string) => {
  const queryClient = useQueryClient();

  return useQuery<undefined, ApiError>(
    getEmailConfirmKeyBuilder(),
    () => commonClient.confirmEmail(userId, token),
    {
      onSuccess: async () => await queryClient.invalidateQueries(getCompanyAndUserInfoKeyBuilder())
    }
  );
};

export const useGetConfigurationQuery = () => {
  return useQuery<ConfigurationItem[], ApiError>(
    getConfigurationKeyBuilder(),
    commonClient.getConfiguration,
    { staleTime: GLOBAL_QUERY_STALE_TIME }
  );
};

export const useGetUserConfigurationQueryV2 = (enabled = true) => {
  return useQuery<ConfigurationItem[], ApiError>({
    queryKey: getConfigurationKeyBuilderV2(),
    queryFn: commonClient.getUserConfigurationV2,
    staleTime: GLOBAL_QUERY_STALE_TIME,
    retry: retryStartupQuery,
    enabled
  });
};

export const useGetCompanyConfigurationQueryV2 = () => {
  return useQuery<ConfigurationItem[], ApiError>(
    getCompanyConfigurationKeyBuilderV2(),
    commonClient.getCompanyConfigurationV2,
    { staleTime: GLOBAL_QUERY_STALE_TIME }
  );
};

export const useEditConfigurationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, ConfigurationItem>(commonClient.editConfiguration, {
    onSuccess: (data, configurationItem) => {
      queryClient.setQueryData<LoggedUserInfo | undefined>(
        getCompanyAndUserInfoKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            company: {
              ...oldData.company,
              configuration: oldData.company.configuration.find(
                (x) => x.key === configurationItem.key
              )
                ? oldData.company.configuration.map((x) => {
                    if (x.key === configurationItem.key) {
                      return configurationItem;
                    }

                    return x;
                  })
                : [...oldData.company.configuration, configurationItem]
            }
          }
      );
    }
  });
};

export const useEditConfigurationMutationV2 = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, UserConfigurationItemToEdit>(
    commonClient.editConfigurationV2,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getConfigurationKeyBuilderV2());
        await queryClient.invalidateQueries(getCompanyConfigurationKeyBuilderV2());
      }
    }
  );
};

export const useUnconfirmEmailAfterChange = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.setQueryData<LoggedUserInfo | undefined>(
      getCompanyAndUserInfoKeyBuilder(),
      (oldData) => oldData && { ...oldData, user: { ...oldData.user, hasEmailConfirmed: false } }
    );
};

export const useGetCountriesQuery = () => {
  return useQuery<PublicCountry[], ApiError>(getCountriesKeyBuilder(), commonClient.getCountries, {
    staleTime: GLOBAL_QUERY_STALE_TIME
  });
};

export const useGetNotificationsQuery = () => {
  return useInfiniteQuery<SignalRNotificationList, ApiError>(
    getNotificationsKeyBuilder(),
    ({ pageParam = 1 }) =>
      commonClient.getNotificationsHistory({
        page: pageParam,
        pageSize: defaultEventLogQuery.pageSize
      }),
    {
      getNextPageParam: (lastPage, pages) =>
        getInfiniteNextPageParam(
          lastPage.items,
          pages.map((page) => page.items)
        )
    }
  );
};

export const useMarkNotificationAsSeenMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string[]>(commonClient.markNotificationsAsSeen, {
    onSuccess: async () => await queryClient.invalidateQueries(getNotificationsKeyBuilder())
  });
};

export const useAddRealtimeNotification = () => {
  const queryClient = useQueryClient();

  return (notification: SignalRNotification) =>
    queryClient.setQueryData<InfiniteData<SignalRNotificationList> | undefined>(
      getNotificationsKeyBuilder(),
      (oldData) =>
        oldData && {
          pageParams: oldData.pageParams,
          pages: oldData.pages.map((page, index) => {
            if (index === 0) {
              return {
                unreadCount: page.unreadCount + 1,
                items: [notification, ...page.items]
              };
            }

            return page;
          })
        }
    );
};

export const useErrorTestQuery = (errorCode?: number, percentage?: string) => {
  return useQuery<undefined, ApiError>(
    ['errorstest'],
    () => commonClient.errorTest(errorCode, percentage),
    {
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useDeleteAttachmentMutation = () => {
  return useMutation<undefined, ApiError, string>(commonClient.deleteAttachment, {
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useGetPowerBIConfigQuery = () => {
  return useQuery<PowerBiEmbeddedTokenResult, ApiError>(
    getPowerBIConfigKeyBuilder(),
    appUserClient.getPowerBIConfig
  );
};

export const useGetFeatureFlagsQuery = (enabled = true) => {
  return useQuery<GetFeatureFlagsQueryResult, ApiError>({
    queryKey: getFeatureFlagsKeyBuilder(),
    queryFn: commonClient.getFeatureFlags,
    staleTime: GLOBAL_QUERY_STALE_TIME,
    retry: retryStartupQuery,
    enabled
  });
};

export const useGetCurrentAnnouncementsQuery = () => {
  return useQuery<Announcement[], ApiError>(
    getCurrentAnnouncementsKeyBuilder(),
    commonClient.getCurrentAnnouncements,
    {
      staleTime: GLOBAL_QUERY_STALE_TIME
    }
  );
};

export const useDismissAnnouncementMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(commonClient.dismissAnnouncement, {
    onSuccess: (_, id) =>
      queryClient.setQueryData<Announcement[] | undefined>(
        getCurrentAnnouncementsKeyBuilder(),
        (oldData) =>
          oldData?.map((announcement) => {
            if (announcement.id === id) {
              return {
                ...announcement,
                isDismissed: true
              };
            }

            return announcement;
          })
      )
  });
};

export const useGetColorPalettesQuery = (enabled = true) => {
  return useQuery<ColorPalette[], string>({
    queryKey: getColorPalettesKeyBuilder(),
    queryFn: () => commonClient.getColorPalettes(),
    staleTime: GLOBAL_QUERY_STALE_TIME,
    enabled
  });
};

export const useGetEventLogsQuery = (params: ListEventLogsParams, enabled: boolean) => {
  return useInfiniteQuery<CappedTableResultOfEventLogEntry, ApiError>({
    queryKey: getTableQueryKey(eventLogsKey, {
      ...params,
      page: params.page || defaultEventLogQuery.page,
      pageSize: params.pageSize || defaultEventLogQuery.pageSize
    }),
    queryFn: ({ pageParam = 1 }) => commonClient.getEventLog({ ...params, page: pageParam }),
    enabled,
    staleTime: 0,
    onError: (error) => handleApiErrorWithNotification(error),
    getNextPageParam: getInfiniteNextPageParamV2
  });
};
